<template>
    <div class="token-checkin-page">
        <!-- Loading state -->
        <div v-if="isValidating" class="validation-state">
            <div class="loading-spinner"></div>
            <h2>Validating your check-in link...</h2>
            <p>Please wait while we verify your access.</p>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="validation-state error-state">
            <div class="error-icon">⚠️</div>
            <h2>Check-in Link Error</h2>
            <p>{{ error }}</p>
            <div class="error-actions">
                <button @click="goToLogin" class="login-btn">Go to Login</button>
            </div>
        </div>

        <!-- Success state with event info -->
        <div v-else-if="tokenData" class="validation-state success-state">
            <div class="success-icon">✅</div>
            <h2>Welcome, {{ tokenData.member.first_name }}!</h2>
            <div class="event-info">
                <h3>{{ tokenData.event.event_type }}</h3>
                <p class="event-date">
                    {{ formatEventDate(tokenData.event.event_date) }}
                </p>
                <p class="venue-info" v-if="tokenData.event.venue_name">
                    📍 {{ tokenData.event.venue_name }}
                    <span v-if="tokenData.event.venue_address">
                        <br>{{ tokenData.event.venue_address }}
                    </span>
                </p>
            </div>
            
            <!-- Show existing check-in info if available -->
            <div v-if="tokenData.existingCheckin" class="existing-checkin-info">
                <p>✅ You have already checked in for this event.</p>
                <p>Click below to update your check-in information.</p>
            </div>
            <div v-else class="checkin-prompt">
                <p>Please complete your check-in for this event.</p>
            </div>

            <div class="token-actions">
                <button @click="openCheckinModal" class="checkin-btn">
                    {{ tokenData.existingCheckin ? 'Update Check-in' : 'Check In Now' }}
                </button>
            </div>
        </div>

        <!-- Check-in Modal -->
        <TokenCheckInModal
            v-if="showCheckinModal"
            :token="token"
            :event="tokenData?.event"
            :member="tokenData?.member"
            :existingCheckIn="tokenData?.existingCheckin"
            @close="closeCheckinModal"
            @check-in="onCheckinSuccess"
        />
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { checkinService } from '../services/checkinService';
import TokenCheckInModal from './TokenCheckInModal.vue';

export default {
    name: 'TokenCheckinPage',
    components: {
        TokenCheckInModal
    },
    setup() {
        const route = useRoute();
        const router = useRouter();
        
        const token = ref(route.params.token);
        const isValidating = ref(true);
        const error = ref(null);
        const tokenData = ref(null);
        const showCheckinModal = ref(false);

        const validateToken = async () => {
            try {
                isValidating.value = true;
                error.value = null;
                
                const data = await checkinService.validateCheckinToken(token.value);
                tokenData.value = data;
                
                // Auto-open modal if no existing check-in
                if (!data.existingCheckin) {
                    setTimeout(() => {
                        showCheckinModal.value = true;
                    }, 1000);
                }
            } catch (err) {
                console.error('Token validation error:', err);
                if (err.response?.status === 401) {
                    error.value = 'This check-in link has expired or is invalid. Please contact your event organizer for a new link.';
                } else if (err.response?.status === 404) {
                    error.value = 'Event not found. The event may have been cancelled or removed.';
                } else {
                    error.value = 'Unable to validate your check-in link. Please try again or contact support.';
                }
            } finally {
                isValidating.value = false;
            }
        };

        const openCheckinModal = () => {
            showCheckinModal.value = true;
        };

        const closeCheckinModal = () => {
            showCheckinModal.value = false;
        };

        const onCheckinSuccess = () => {
            showCheckinModal.value = false;
            // Refresh token data to get updated check-in info
            validateToken();
        };

        const goToLogin = () => {
            router.push('/login');
        };

        const formatEventDate = (dateString) => {
            const date = new Date(dateString);
            return date.toLocaleString('en-AU', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
                timeZone: 'Australia/Brisbane'
            });
        };

        onMounted(() => {
            if (token.value) {
                validateToken();
            } else {
                error.value = 'No check-in token provided.';
                isValidating.value = false;
            }
        });

        return {
            token,
            isValidating,
            error,
            tokenData,
            showCheckinModal,
            openCheckinModal,
            closeCheckinModal,
            onCheckinSuccess,
            goToLogin,
            formatEventDate
        };
    }
};
</script>

<style scoped>
.token-checkin-page {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    padding: 1rem;
}

.validation-state {
    background: white;
    padding: 3rem;
    border-radius: 16px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    text-align: center;
    max-width: 500px;
    width: 100%;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #4285f4;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 1.5rem;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.success-icon, .error-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
}

.error-state {
    border-left: 4px solid #dc3545;
}

.success-state {
    border-left: 4px solid #28a745;
}

.event-info {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 8px;
    margin: 1.5rem 0;
    text-align: left;
}

.event-info h3 {
    margin: 0 0 0.5rem 0;
    color: #495057;
}

.event-date {
    font-size: 1.1rem;
    font-weight: 500;
    color: #6c757d;
    margin: 0.5rem 0;
}

.venue-info {
    color: #6c757d;
    margin: 0.5rem 0 0 0;
    line-height: 1.4;
}

.existing-checkin-info {
    background: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
    padding: 1rem;
    border-radius: 8px;
    margin: 1rem 0;
}

.checkin-prompt {
    background: #e2e3e5;
    border: 1px solid #d3d3d4;
    color: #383d41;
    padding: 1rem;
    border-radius: 8px;
    margin: 1rem 0;
}

.token-actions, .error-actions {
    margin-top: 2rem;
}

.checkin-btn, .login-btn {
    background: #4285f4;
    color: white;
    border: none;
    padding: 1rem 2rem;
    border-radius: 8px;
    font-size: 1.1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
}

.checkin-btn:hover, .login-btn:hover {
    background: #357abd;
}

.login-btn {
    background: #6c757d;
}

.login-btn:hover {
    background: #5a6268;
}

h2 {
    color: #343a40;
    margin-bottom: 1rem;
}

p {
    color: #6c757d;
    line-height: 1.5;
    margin-bottom: 0.5rem;
}

@media (max-width: 768px) {
    .validation-state {
        padding: 2rem;
        margin: 1rem;
    }
    
    .success-icon, .error-icon {
        font-size: 2rem;
    }
}
</style>